import React from 'react';
import { Box, Typography } from '@mui/material';
import DataTableModule from '@/__core/layout/module/DataTableModule';

interface MonthlySummaryItem {
  month: string;
  income: number;
  expense: number;
  cashBalance: number;
}

interface SupplierDebtDetailItem {
  supplierId: string;
  supplierName: string;
  debtAmount: number;
}

interface FinancialOverviewRow2Props {
  monthlySummary: MonthlySummaryItem[];
  supplierDebtDetails: SupplierDebtDetailItem[];
  formatVnd: (value: number) => string;
  onCellClick?: (month: string, type: 'receipt' | 'payment') => void;
  onSupplierDebtClick?: (supplierId: string, supplierName: string) => void;
}

interface MonthlyTableRow {
  id: string;
  month: string;
  income: number | '';
  expense: number | '';
  cashBalance: number | '';
}

interface SupplierTableRow {
  id: string;
  supplierId: string;
  supplierName: string;
  debtAmount: number | '';
}

export default function FinancialOverviewRow2({
  monthlySummary,
  supplierDebtDetails,
  formatVnd,
  onCellClick,
  onSupplierDebtClick,
}: FinancialOverviewRow2Props) {
  const formatTableAmount = React.useCallback((value: unknown) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue === 0) {
      return '-';
    }

    return formatVnd(numericValue);
  }, [formatVnd]);

  const leftTableWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const rightTableWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [syncedTableHeight, setSyncedTableHeight] = React.useState<number>(0);

  React.useEffect(() => {
    if (!leftTableWrapperRef.current || !rightTableWrapperRef.current) {
      return;
    }

    const leftElement = leftTableWrapperRef.current;
    const rightElement = rightTableWrapperRef.current;

    const syncHeight = () => {
      const leftHeight = leftElement.getBoundingClientRect().height;
      const rightHeight = rightElement.getBoundingClientRect().height;
      setSyncedTableHeight(Math.max(leftHeight, rightHeight));
    };

    syncHeight();

    const observer = new ResizeObserver(() => {
      syncHeight();
    });

    observer.observe(leftElement);
    observer.observe(rightElement);

    return () => {
      observer.disconnect();
    };
  }, [monthlySummary.length, supplierDebtDetails.length]);

  const monthlyColumns = React.useMemo(() => {
    return [
      {
        id: 'month',
        label: 'Tháng',
        width: '60px',
        borderRight: true,
        render: (value: unknown) => <Typography variant='body2'>{String(value || '-')}</Typography>,
      },
      {
        id: 'income',
        label: 'Thu',
        width: '160px',
        headerAlign: 'center' as const,
        align: 'right' as const,
        render: (value: unknown, row: any) => (
          <Typography
            variant='body2'
            onClick={() => onCellClick?.(row.month, 'receipt')}
            sx={onCellClick ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}
          >
            {formatTableAmount(value)}
          </Typography>
        ),
      },
      {
        id: 'expense',
        label: 'Chi',
        width: '160px',
        headerAlign: 'center' as const,
        align: 'right' as const,
        render: (value: unknown, row: any) => (
          <Typography
            variant='body2'
            onClick={() => onCellClick?.(row.month, 'payment')}
            sx={onCellClick ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}
          >
            {formatTableAmount(value)}
          </Typography>
        ),
      },
      {
        id: 'cashBalance',
        label: 'Số dư',
        width: '160px',
        headerAlign: 'center' as const,
        align: 'right' as const,
        render: (value: unknown) => <Typography variant='body2'>{formatTableAmount(value)}</Typography>,
      },
    ];
  }, [formatTableAmount, onCellClick]);

  const monthlyData = React.useMemo<MonthlyTableRow[]>(() => {
    return monthlySummary.map((row, index) => ({
      id: `${row.month}-${index}`,
      month: row.month,
      income: row.income,
      expense: row.expense,
      cashBalance: row.cashBalance,
    }));
  }, [monthlySummary]);

  const supplierColumns = React.useMemo(() => {
    return [
      {
        id: 'supplierName',
        label: 'Nhà cung cấp',
        width: '220px',
        render: (value: unknown) => <Typography variant='body2'>{String(value || '-')}</Typography>,
      },
      {
        id: 'debtAmount',
        label: 'Công nợ',
        width: '180px',
        align: 'right' as const,
        render: (value: unknown, row: any) => (
          <Typography
            variant='body2'
            sx={{
              fontWeight: 600,
              ...(onSupplierDebtClick && row?.supplierId ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}),
            }}
            onClick={() => {
              if (onSupplierDebtClick && row?.supplierId) {
                onSupplierDebtClick(String(row.supplierId), String(row.supplierName || ''));
              }
            }}
          >
            {formatTableAmount(value)}
          </Typography>
        ),
      },
    ];
  }, [formatTableAmount, onSupplierDebtClick]);

  const supplierData = React.useMemo<SupplierTableRow[]>(() => {
    return supplierDebtDetails.map((supplier, index) => ({
      id: `${supplier.supplierName}-${index}`,
      supplierId: supplier.supplierId,
      supplierName: supplier.supplierName,
      debtAmount: supplier.debtAmount,
    }));
  }, [supplierDebtDetails]);

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          gap: 3,
        }}
      >
      <Box
        sx={{
          width: { xs: '100%', md: 'fit-content' },
          flex: { xs: '1 1 100%', md: '0 0 auto' },
        }}
      >
          <Typography variant='h6' gutterBottom>
            Chi tiết theo tháng
          </Typography>
          <Box
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                minHeight: syncedTableHeight > 0 ? `${syncedTableHeight}px` : undefined,
              },
            }}
            ref={leftTableWrapperRef}
          >
            <DataTableModule
              data={monthlyData}
              columns={monthlyColumns}
              tableSx={{
                minWidth: 'max-content',
                width: 'max-content',
              }}
              tableContainerSx={{
                width: { xs: '100%', md: 'fit-content' },
                maxWidth: { xs: '100%', md: 'fit-content' },
                overflowX: 'auto',
              }}
              tableLayout='fixed'
              onRowSelect={() => undefined}
              onSelectAll={() => undefined}
              getRowId={(row) => String((row as any).id || '')}
              isRowSelected={() => false}
              areAllRowsSelected={false}
              areSomeRowsSelected={false}
              showCheckbox={false}
              reserveLeftFilterSpace={false}
            />
          </Box>
      </Box>

      <Box
        sx={{
          width: { xs: '100%', md: 'fit-content' },
          flex: { xs: '1 1 100%', md: '0 0 auto' },
        }}
      >
          <Typography variant='h6' gutterBottom>
            Công nợ nhà cung cấp
          </Typography>
          <Box
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                minHeight: syncedTableHeight > 0 ? `${syncedTableHeight}px` : undefined,
              },
            }}
            ref={rightTableWrapperRef}
          >
            <DataTableModule
              data={supplierData}
              columns={supplierColumns}
              tableLayout='fixed'
              tableSx={{
                minWidth: 'max-content',
                width: 'max-content',
              }}
              tableContainerSx={{
                width: { xs: '100%', md: 'fit-content' },
                maxWidth: { xs: '100%', md: 'fit-content' },
                overflowX: 'auto',
              }}
              onRowSelect={() => undefined}
              onSelectAll={() => undefined}
              getRowId={(row) => String((row as any).id || '')}
              isRowSelected={() => false}
              areAllRowsSelected={false}
              areSomeRowsSelected={false}
              showCheckbox={false}
              reserveLeftFilterSpace={false}
            />
          </Box>
      </Box>
      </Box>
    </>
  );
}
